import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../../config/api";
import { FaFilter, FaUser, FaCheckCircle, FaExclamationCircle, FaTimesCircle, FaClock } from "react-icons/fa";
import AOS from "aos";
import { useAuth } from "../../../context/AuthContext";
import "aos/dist/aos.css";
import axios from "axios";

function Listedprojectscontainer() {
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
    AOS.init({ duration: 800 });
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/thrustarea/${user?.coordinatorFor}`,
        {
          headers: {
            "Authorization": `Bearer ${user?.token}`
          }
        }
      );
      const data = response.data;
      // console.log(data);
      if (response.status === 200) {
        setProjects(data.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (id) => {
    navigate(`/project/${id._id}`);
  };

  // Filter projects based on selected status
  const filteredProjects = projects.filter((project) => {
    if (selectedStatus === "All") return true;

    const status = project.status?.toLowerCase();

    if (selectedStatus === "Pending") {
      return status === "pending";
    }
    if (selectedStatus === "Approved") {
      return status === "approved";
    }
    if (selectedStatus === "Needs Modification") {
      return status === "needs-modifications";
    }
    if (selectedStatus === "Rejected") {
      return status === "rejected";
    }
    if (selectedStatus === "Guide Assigned") {
      return status === "guide-assigned";
    }

    return status === selectedStatus.toLowerCase();
  });

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200"><FaCheckCircle className="text-[10px]" /> Approved</span>;
      case "rejected":
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200"><FaTimesCircle className="text-[10px]" /> Rejected</span>;
      case "pending":
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200"><FaClock className="text-[10px]" />Pending</span>;
      case "guide-assigned":
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200"><FaUser className="text-[10px]" />Guide Assigned</span>;
      case "needs-modifications":
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200"><FaExclamationCircle className="text-[10px]" /> Needs Modification</span>;
      default:
        return <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200"><FaExclamationCircle className="text-[10px]" /> {status}</span>;
    }
  };

  const statuses = ["All", "Pending", "Approved", "Needs Modification", "Rejected", "Guide Assigned"];

  return (
    <div className="min-h-screen p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8" data-aos="fade-down">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Listed Projects</h1>
          <p className="mt-1 text-gray-500 text-sm md:text-base">Review and manage student project submissions.</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">Loading projects...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* Status Filter */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100" data-aos="fade-down" data-aos-delay="100">
              <div className="flex items-center gap-2 mb-3 text-gray-800 font-semibold px-1">
                <FaFilter className="text-[#B7202E]" /> Filter by Status
              </div>
              <div className="flex flex-wrap gap-2">
                {statuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => setSelectedStatus(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${selectedStatus === status
                      ? "bg-[#B7202E] text-white border-[#B7202E] shadow-md transform scale-105"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Project List */}
            <div>
              <div className="flex justify-between items-center mb-4 text-sm text-gray-600 font-medium px-1">
                <span>Showing {filteredProjects.length} projects</span>
                {selectedStatus !== "All" && (
                  <span className="bg-gray-200 px-2 py-1 rounded text-xs text-gray-700">
                    Filtered by: {selectedStatus}
                  </span>
                )}
              </div>

              {filteredProjects.length > 0 ? (
                <div className="grid gap-4">
                  {filteredProjects.map((listedproject, index) => (
                    <div
                      key={index}
                      className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300 group relative overflow-hidden"
                      data-aos="fade-up"
                      data-aos-delay={index * 50}
                    >
                      <div className="absolute top-0 left-0 w-1 h-full bg-[#B7202E] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 group-hover:text-[#B7202E] transition-colors leading-tight">
                            {listedproject.projectTitle}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded border border-gray-100">
                              <FaUser className="text-xs text-gray-400" /> {listedproject.userId}
                            </span>
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs border border-gray-200">
                              {listedproject.domain.preference1.department}
                            </span>
                            {getStatusBadge(listedproject.status)}
                          </div>
                        </div>
                        <div className="flex-shrink-0 pt-2 md:pt-0">
                          <button
                            className="w-full md:w-auto bg-[#B7202E] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#961925] hover:shadow-lg hover:-translate-y-0.5 transition-all shadow-sm focus:ring-2 focus:ring-[#B7202E]/20 active:scale-95"
                            onClick={() => handleReviewClick(listedproject)}
                          >
                            Review Project
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100" data-aos="fade-up">
                  <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-400">
                    <FaFilter className="text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">No projects found</h3>
                  <p className="text-gray-500">Try adjusting your filters to see more results.</p>
                  <button
                    onClick={() => setSelectedStatus("All")}
                    className="mt-4 text-[#B7202E] font-semibold hover:underline text-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Listedprojectscontainer;
